import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Item } from "@/lib/types";

export function useSubtasks(parentId: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subtasks", parentId],
    queryFn: async () => {
      if (!user || !parentId) return [];
      const { data, error } = await supabase
        .from("items")
        .select("*")
        .eq("parent_id", parentId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as Item[];
    },
    enabled: !!user && !!parentId,
  });
}

export function useSubtaskCounts(parentIds: string[]) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["subtask-counts", ...parentIds],
    queryFn: async () => {
      if (!user || parentIds.length === 0) return {};
      const { data, error } = await supabase
        .from("items")
        .select("id, parent_id, status")
        .in("parent_id", parentIds);
      if (error) throw error;

      const counts: Record<string, { done: number; total: number }> = {};
      for (const item of data) {
        if (!item.parent_id) continue;
        if (!counts[item.parent_id]) counts[item.parent_id] = { done: 0, total: 0 };
        counts[item.parent_id].total++;
        if (item.status === "done") counts[item.parent_id].done++;
      }
      return counts;
    },
    enabled: !!user && parentIds.length > 0,
  });
}

export function useCreateSubtask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      parent_id: string;
      title: string;
      project_tag?: string | null;
      date_target?: string | null;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("items")
        .insert({
          user_id: user.id,
          title: input.title,
          raw_input: input.title,
          parent_id: input.parent_id,
          status: "active",
          priority: "medium",
          source: "manual",
          sort_order: 0,
          project_tag: input.project_tag || null,
          date_target: input.date_target || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as Item;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["subtasks", variables.parent_id] });
      queryClient.invalidateQueries({ queryKey: ["subtask-counts"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useUpdateSubtask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("items")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["subtask-counts"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
    },
  });
}
